import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { usePfmKpis, usePfmData } from "../../../prop-firm-manager/PfmDataContext";
import { CURRENCY } from "../../../shared/currency";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function KpiTile({
  label, value, sub, color, isDark,
}: { label: string; value: string; sub?: string; color?: string; isDark: boolean }) {
  return (
    <div className="rounded-lg px-3 py-2 bg-muted/40 flex flex-col justify-center min-w-0 h-full overflow-hidden">
      <p className={`text-[10px] font-semibold tracking-widest uppercase mb-1.5 truncate widget-label`}>
        {label}
      </p>
      <p className="text-xl font-bold font-mono tabular-nums leading-none truncate" style={{ color: color ?? "inherit" }}>
        {value}
      </p>
      {sub && <p className="text-[11px] text-muted-foreground mt-1 truncate">{sub}</p>}
    </div>
  );
}

function fmt(amount: number) {
  return new Intl.NumberFormat(undefined, {
    style: "currency", currency: CURRENCY, minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(amount);
}

export default function PropFirmKpisWidget() {
  const { isDark, t } = usePfmData();
  const kpis = usePfmKpis();

  const pos = "hsl(var(--edgely-pnl-positive))";
  const neg = "hsl(var(--edgely-pnl-negative))";
  const accent = "hsl(var(--primary))";
  const roiColor = kpis.roi > 0 ? pos : kpis.roi < 0 ? neg : undefined;
  const netColor = kpis.netProfit > 0 ? pos : kpis.netProfit < 0 ? neg : undefined;

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader label={t.dashboard.widgets.propfirmKpis} isDark={isDark} compact />
      <CardContent className="flex-1 p-3 min-h-0">
        <div className="grid grid-cols-6 gap-2 h-full">
          <KpiTile label={t.propFirmManager.totalPayouts} value={fmt(kpis.totalPayouts)} color={accent} isDark={isDark} />
          <KpiTile label={t.propFirmManager.totalExpenses} value={fmt(kpis.totalExpenses)} color={kpis.totalExpenses > 0 ? neg : undefined} isDark={isDark} />
          <KpiTile label={t.propFirmManager.netProfit} value={`${kpis.netProfit >= 0 ? "+" : ""}${fmt(kpis.netProfit)}`} color={netColor} isDark={isDark} />
          <KpiTile label={t.propFirmManager.roiGlobal} value={`${kpis.roi >= 0 ? "+" : ""}${kpis.roi.toFixed(1)}%`} color={roiColor} isDark={isDark} />
          <KpiTile label={t.propFirmManager.activeAccounts} value={String(kpis.activeCount)} isDark={isDark} />
          <KpiTile
            label={t.propFirmManager.bestRoi}
            value={kpis.bestRoi ? `${kpis.bestRoi.roi >= 0 ? "+" : ""}${kpis.bestRoi.roi.toFixed(1)}%` : "—"}
            sub={kpis.bestRoi ? kpis.bestRoi.name : t.propFirmManager.noBestRoi}
            color={kpis.bestRoi && kpis.bestRoi.roi >= 0 ? accent : undefined}
            isDark={isDark}
          />
        </div>
      </CardContent>
    </Card>
  );
}
